import React, { useState, useEffect, useRef, useCallback } from "react"; 
import * as d3 from "d3"; 
import api from "../services/api"; 
import Modal from "react-modal"; 
import { useNavigate, useParams } from "react-router-dom"; 
import styled, { keyframes, css } from "styled-components"; 

// ============ ANIMACIONES ============ 
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// ============ COMPONENTES ESTILIZADOS ============ 
const ContainerGrafos = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  overflow: hidden;
  color: white;
  
  .light-mode & {
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    color: #1e293b;
  }

  @media (max-width: 760px) {
    width: 100%;
    min-width: 0;
    height: 100dvh;

    .graph-controls-section {
      flex: 0 0 auto;
      min-width: 0;
      padding: 8px !important;
    }

    .graph-main-section {
      flex: 1 1 auto !important;
      min-width: 0;
      min-height: 0;
      padding: 6px !important;
    }
  }
`;

const Section = styled.div`
  padding: ${props => props.compact ? '15px 20px' : '20px'};
  background: ${props => props.background || 'transparent'};
  border-bottom: ${props => props.border ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'};
  box-shadow: ${props => props.shadow ? '0 2px 20px rgba(0, 0, 0, 0.3)' : 'none'};
  backdrop-filter: ${props => props.blur ? 'blur(10px)' : 'none'};
  position: relative;
  z-index: ${props => props.zIndex || 1};
  
  .light-mode & {
    border-bottom: ${props => props.border ? '1px solid rgba(0, 0, 0, 0.1)' : 'none'};
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: 760px) {
    width: 100%;
    flex-wrap: nowrap;
    gap: 10px;
    align-items: flex-end;
    overflow-x: auto;
    padding-bottom: 5px;
    scroll-snap-type: x proximity;
    scrollbar-width: thin;
  }
`;

const ButtonBack = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 12px 28px;
  border-radius: 30px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.3px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(59, 130, 246, 0.5);
  }
  
  &:active {
    transform: translateY(0);
  }

  @media (max-width: 760px) {
    flex: 0 0 auto;
    min-height: 44px;
    padding: 10px 14px;
    font-size: 12px;
    white-space: nowrap;
    scroll-snap-align: start;
  }
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;

  @media (max-width: 760px) {
    flex: 0 0 165px;
    min-width: 165px;
    scroll-snap-align: start;
  }
`;

const ControlLabel = styled.label`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  .light-mode & {
    color: #64748b;
  }
`;

const ControlSelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #334155;
  border-radius: 14px;
  background: rgba(30, 41, 59, 0.8);
  color: white;
  font-size: 14px;
  transition: all 0.3s ease;
  outline: none;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 16px;
  padding-right: 40px;
  
  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }
  
  &:hover {
    border-color: #475569;
  }
  
  option {
    background: #1e293b;
    color: white;
  }
  
  .light-mode & {
    border-color: #cbd5e1;
    background: rgba(255, 255, 255, 0.9);
    color: #1e293b;
    
    option {
      background: white;
      color: #1e293b;
    }
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
  padding: 8px 0;
  color: #cbd5e1;
  font-weight: 500;
  
  .light-mode & {
    color: #475569;
  }
`;

const CheckboxInput = styled.input.attrs({ type: 'checkbox' })`
  display: none;
  
  &:checked + span {
    background: #3b82f6;
    border-color: #3b82f6;
    
    &::after {
      opacity: 1;
    }
  }
`;

const CheckboxCustom = styled.span`
  width: 22px;
  height: 22px;
  border: 2px solid #475569;
  border-radius: 7px;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::after {
    content: '✓';
    color: white;
    font-size: 12px;
    font-weight: bold;
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  
  &:hover {
    border-color: #64748b;
  }
  
  .light-mode & {
    border-color: #cbd5e1;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
  animation: ${fadeIn} 0.3s ease;
  
  .light-mode & {
    background: rgba(248, 250, 252, 0.95);
  }
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 3px solid rgba(59, 130, 246, 0.1);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 20px;
`;

const LoadingText = styled.p`
  color: #94a3b8;
  font-weight: 500;
  font-size: 16px;
  margin: 0;
  
  .light-mode & {
    color: #64748b;
  }
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%);
  color: white;
  padding: 16px 28px;
  border-radius: 14px;
  font-weight: 500;
  z-index: 1000;
  box-shadow: 0 8px 25px rgba(127, 29, 29, 0.3);
  border: 1px solid rgba(127, 29, 29, 0.5);
  display: flex;
  align-items: center;
  gap: 10px;
  animation: ${fadeIn} 0.3s ease;
  
  &::before {
    content: '⚠️';
    font-size: 18px;
  }
`;

const LegendContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(30, 41, 59, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  z-index: 10;
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: ${slideIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  max-width: 300px;
  
  .light-mode & {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 760px) {
    top: 6px;
    right: 6px;
    left: 6px;
    box-sizing: border-box;
    max-width: none;
    padding: 8px 10px;
    border-radius: 12px;
  }
`;

const LegendTitle = styled.h4`
  margin: 0 0 15px 0;
  font-size: 14px;
  font-weight: 700;
  color: #e2e8f0;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '📊';
    font-size: 16px;
  }
  
  .light-mode & {
    color: #1e293b;
  }

  @media (max-width: 760px) {
    margin-bottom: 4px;
    font-size: 10px;
  }
`;

const LegendItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 8px;

  @media (max-width: 760px) {
    flex-direction: row;
    gap: 12px;
    max-height: none;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0 0 2px;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #475569;
    border-radius: 3px;
  }
  
  .light-mode & {
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.1);
    }
    
    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
    }
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.05);
  
  &:hover {
    background: rgba(59, 130, 246, 0.15);
    transform: translateX(4px);
  }
  
  .light-mode & {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const LegendColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  box-shadow: 0 0 15px ${props => props.color};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.3);
  }
  
  .light-mode &::after {
    border: 1px solid rgba(0, 0, 0, 0.3);
  }
`;

const LegendText = styled.span`
  font-size: 14px;
  color: #e2e8f0;
  font-weight: 500;
  flex: 1;
  
  .light-mode & {
    color: #1e293b;
  }
`;

const GraphContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  
  svg {
    width: 100%;
    height: 100%;
    min-height: 600px;
  }

  @media (max-width: 760px) {
    min-height: 0;

    svg {
      min-height: 100%;
    }
  }
`;

const TimelineControl = styled.div`
  background: rgba(30, 41, 59, 0.9);
  border-radius: 20px;
  padding: 16px 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: ${fadeIn} 0.5s ease;
  width: 100%;
  min-height: auto;
  
  .light-mode & {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.1);
  }
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const TimelineTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #3b82f6;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: '📈';
    font-size: 18px;
  }
`;

const TimelineDate = styled.span`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 8px 20px;
  border-radius: 30px;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
`;

const PlayControls = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
`;

const PlayButton = styled.button`
  background: ${props => props.playing ? '#10b981' : '#3b82f6'};
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 25px rgba(59, 130, 246, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SpeedControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #94a3b8;
  font-size: 14px;
  
  .light-mode & {
    color: #64748b;
  }
`;

const SpeedButton = styled.button`
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
  border: 1px solid #3b82f6;
  padding: 6px 15px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(59, 130, 246, 0.3);
  }
  
  &.active {
    background: #3b82f6;
    color: white;
  }
`;

const TimelineSlider = styled.input.attrs({ type: 'range' })`
  width: 100%;
  height: 6px;
  -webkit-appearance: none;
  background: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 10px;
  outline: none;
  margin: 15px 0;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    border: 3px solid #3b82f6;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
    transition: all 0.3s ease;
    
    &:hover {
      transform: scale(1.2);
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.6);
    }
  }
`;

const TimelineLabels = styled.div`
  display: flex;
  justify-content: space-between;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 500;
  margin-top: 8px;
  
  .light-mode & {
    color: #64748b;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid #334155;

`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #1e293b;
  font-size: 22px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;

`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.1);
  }
`;

const PersonaHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 20px;

`;

const PersonaAvatar = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.color} 0%, ${props => props.color}80 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: white;
  box-shadow: 0 8px 25px ${props => props.color}80;

`;

const PersonaTitle = styled.div`
  flex: 1;
  
  h4 {
    margin: 0 0 8px 0;
    font-size: 24px;
    color: #1e293b;
    font-weight: 700;
  }

`;

const PersonaStatus = styled.span`
  background: ${props => props.color || '#3b82f6'};
  color: white;
  padding: 6px 16px;
  border-radius: 30px;
  font-size: 13px;
  font-weight: 600;
  display: inline-block;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 20px;

`;

const InfoItem = styled.div`
  padding: 18px;
  background: ${props => props.fullWidth ? '#f8fafc' : 'white'};
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;
  grid-column: ${props => props.fullWidth ? '1 / -1' : 'auto'};

  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);
    border-color: #e2e8f0;
  }
`;

const InfoLabel = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoValue = styled.span`
  display: block;
  font-size: 16px;
  color: #1e293b;
  font-weight: 500;
`;

const ControlSectionCard = styled.div`
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px;
  display: grid;
  gap: 14px;
`;

const ControlSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #0f172a;
  font-size: 15px;
  font-weight: 700;
`;

const ControlSectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;

`;

const ControlDetailCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
  display: grid;
  gap: 6px;
`;

const ControlDetailLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const ControlDetailValue = styled.span`
  font-size: 14px;
  color: #0f172a;
  font-weight: 500;
  line-height: 1.45;
`;

const InlineSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f1f5f9;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: ${() => css`${spin} 1s linear infinite`};
  margin-bottom: 20px;
`;

const ThemeToggleButton = styled.button`
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 30px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }
  
  .light-mode & {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  }
`;

// ============ COMPONENTE PRINCIPAL ============ 
const Grafos = () => {
  const { idPersona } = useParams();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [relacionesModalOpen, setRelacionesModalOpen] = useState(false);
  const [seguimientoModalOpen, setSeguimientoModalOpen] = useState(false);
  const [seguimientoLoading, setSeguimientoLoading] = useState(false);
  const [ultimoControlData, setUltimoControlData] = useState(null);
  const [seguimientoModalError, setSeguimientoModalError] = useState("");
  const [relacionesData, setRelacionesData] = useState([]);
  const [data, setData] = useState({ nodes: [], links: [] });
  const [filteredData, setFilteredData] = useState({ nodes: [], links: [] });
  const [excluirFechasNulas, setExcluirFechasNulas] = useState(false);
  const [loading, setLoading] = useState(false);
  const [relacionesLoading, setRelacionesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1);
  const [fechaInicial, setFechaInicial] = useState(() => {
    const fecha = new Date();
    fecha.setMonth(fecha.getMonth() - 6);
    return fecha.toISOString().split('T')[0];
  });
  const [fechaFinal, setFechaFinal] = useState(() => {
    const fecha = new Date();
    fecha.setMonth(fecha.getMonth() + 6);
    return fecha.toISOString().split('T')[0];
  });
  const [fechaSeleccionada, setFechaSeleccionada] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [tipoFecha, setTipoFecha] = useState('FechaPrimeraConsulta');
  const [visibleNodes, setVisibleNodes] = useState([]);
  const [lightMode, setLightMode] = useState(false);

  const svgRef = useRef();
  const simulationRef = useRef();
  const playIntervalRef = useRef();
  const navigate = useNavigate();

  // Cargar todas las personas desde el endpoint
  const cargarTodasPersonas = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/leerPersonasEnGrafos', {
        params: {
          tipoFecha,
          fechaSeleccionada: '2999-12-31',
          excluirFechasNulas,
        },
      });
      
      console.log('Datos recibidos del endpoint:', response.data);

      const personas = response.data.personas || [];
      const relaciones = response.data.relaciones || [];

      // Crear nodos usando la fecha real definida por tipoFecha en backend.
      const nodes = personas.map((persona) => {
        return {
          id: persona.IdPersona,
          name: `${persona.Nombre} ${persona.Apellido}`.trim(),
          color: persona.ColorAsociado || "#3b82f6",
          estado: persona.estadoNombre || "Sin estado",
          informacionCompleta: persona,
          fechaCercana: persona.fechaSeguimiento || null,
          isSelected: persona.IdPersona === parseInt(idPersona),
          riesgo: "medio"
        };
      });

      // Crear links - CORREGIDO: Asegurar que sean objetos consistentes
      const links = relaciones.map((rel) => ({
        source: rel.source,
        target: rel.target,
        color: lightMode ? "rgba(100, 116, 139, 0.6)" : "rgba(148, 163, 184, 0.6)"
      }));

      console.log('Nodos creados:', nodes.length);
      console.log('Links creados:', links.length);

      setData({ nodes, links });
      
      // Calcular fechas para el timeline
      if (nodes.length > 0) {
        const fechas = nodes
          .map(n => n.fechaCercana ? new Date(n.fechaCercana) : null)
          .filter(d => d)
          .sort((a, b) => a - b);
        
        if (fechas.length > 0) {
          const primeraFecha = fechas[0];
          const ultimaFecha = fechas[fechas.length - 1];
          
          setFechaInicial(primeraFecha.toISOString().split('T')[0]);
          setFechaFinal(ultimaFecha.toISOString().split('T')[0]);
          setFechaSeleccionada(ultimaFecha.toISOString().split('T')[0]);
        }
      }
      
      // Inicialmente mostrar todos los nodos
      setFilteredData({ nodes, links });
      setVisibleNodes(nodes);
    } catch (error) {
      console.error("Error al cargar personas:", error);
      setError("Error al cargar los datos. Verifica la conexión con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  // Filtrar por fecha para la animación - CORREGIDO
  const filtrarPorFecha = useCallback((fecha) => {
    if (data.nodes.length === 0) return;
    
    const fechaSel = new Date(fecha);
    
    const filteredNodes = data.nodes.filter((persona) => {
      if (!persona.fechaCercana && excluirFechasNulas) return false;
      if (!persona.fechaCercana) return true;
      
      const fechaPersona = new Date(persona.fechaCercana);
      return fechaPersona <= fechaSel;
    });

    // Filtrar links donde ambos nodos estén visibles
const nodeIds = new Set(filteredNodes.map(n => n.id));
const filteredLinks = data.links.filter(link => 
  nodeIds.has(typeof link.source === 'object' ? link.source.id : link.source) &&
  nodeIds.has(typeof link.target === 'object' ? link.target.id : link.target)
    );

    console.log('Nodos filtrados:', filteredNodes.length);
    console.log('Links filtrados:', filteredLinks.length);

    setFilteredData({ 
       nodes: filteredNodes, 
  links: filteredLinks // ahora solo los links entre nodos visibles
    });
    setVisibleNodes(filteredNodes);
  }, [data, excluirFechasNulas]);

  // Cargar relaciones de una persona
  const cargarRelacionesPersona = async (personaId) => {
    setRelacionesLoading(true);
    try {
      const response = await api.get(`/relaciones/${personaId}`);
      setRelacionesData(response.data || []);
      highlightRelations(personaId, response.data);
    } catch (error) {
      console.error("Error al cargar relaciones:", error);
      setError("No se pudieron cargar las relaciones");
    } finally {
      setRelacionesLoading(false);
    }
  };

  // Destacar relaciones
  const highlightRelations = (personaId, relaciones) => {
    const svg = d3.select(svgRef.current);
    if (!svg.selectAll) return;
    
    const nodeIds = new Set([personaId, ...relaciones.map(r => r.personaRelacionada?.IdPersona).filter(id => id)]);

    svg.selectAll("circle.node-circle")
      .attr("stroke", d => nodeIds.has(d.id) ? "#ffffff" : (lightMode ? "rgba(0, 0, 0, 0.2)" : "rgba(255, 255, 255, 0.3)"))
      .attr("stroke-width", d => nodeIds.has(d.id) ? 3 : 1)
      .attr("filter", d => nodeIds.has(d.id) ? 
        "drop-shadow(0 0 25px rgba(59, 130, 246, 0.8))" : 
        (lightMode ? "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2))" : "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3))"));
  };

  // Avanzar fecha automáticamente
  const avanzarFecha = () => {
    const currentDate = new Date(fechaSeleccionada);
    const nextDate = new Date(currentDate);
    nextDate.setDate(currentDate.getDate() + 1);
    const finalDate = new Date(fechaFinal);
    
    if (nextDate > finalDate) {
      setIsPlaying(false);
      return;
    }
    
    const newFecha = nextDate.toISOString().split('T')[0];
    setFechaSeleccionada(newFecha);
  };

  // Controlar play/pause
  useEffect(() => {
    if (isPlaying) {
      playIntervalRef.current = setInterval(avanzarFecha, 1000 / playSpeed);
    } else {
      clearInterval(playIntervalRef.current);
    }
    return () => {
      clearInterval(playIntervalRef.current);
    };
  }, [isPlaying, playSpeed, fechaSeleccionada]);

  // Filtrar cuando cambia la fecha seleccionada
  useEffect(() => {
    if (data.nodes.length > 0) {
      filtrarPorFecha(fechaSeleccionada);
    }
  }, [fechaSeleccionada, data, filtrarPorFecha]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSpeedChange = (speed) => {
    setPlaySpeed(speed);
  };

  const handleClick = () => navigate("../ListadoCasosContactos");

  const toggleTheme = () => {
    setLightMode(!lightMode);
  };

  // Cargar datos cuando cambia el tipo de fecha o el criterio de fechas nulas.
  useEffect(() => {
    cargarTodasPersonas();
  }, [tipoFecha, excluirFechasNulas]);

  // Obtener colores únicos para la leyenda
  const getUniqueNodeColors = (nodes) => {
    const uniqueColors = nodes.map((node) => ({
      color: node.color,
      estado: node.estado
    }));
    
    const unique = uniqueColors.filter((value, index, self) =>
      index === self.findIndex((t) => (
        t.color === value.color && t.estado === value.estado
      ))
    );
    
    return unique;
  };

  // Manejar clic en nodo
  const handleNodeClick = useCallback(async (nodeData) => {
    setSelectedNode(nodeData);
    setModalIsOpen(true);
    
    // Cargar relaciones de la persona
    try {
      setRelacionesLoading(true);
      const response = await api.get(`/relaciones/${nodeData.id}`);
      setRelacionesData(response.data || []);
      
      // Destacar relaciones en el gráfico
      highlightRelations(nodeData.id, response.data);
        
    } catch (error) {
      console.error("Error al cargar relaciones:", error);
    } finally {
      setRelacionesLoading(false);
    }
  }, []);

  // Renderizar gráfico - CORREGIDO PARA MOSTRAR LÍNEAS Y NOMBRES COMPLETOS
  useEffect(() => {
    if (filteredData.nodes.length === 0) return;

    const width = 2500, height = 1000;
    
    // Detener simulación anterior
    if (simulationRef.current) {
      simulationRef.current.stop();
    }

    const svg = d3.select(svgRef.current)
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", [0, 0, width, height])
      .attr("style", "max-width: 100%; height: auto;");

    // Limpiar SVG
    svg.selectAll("*").remove();

    // Fondo
    svg.append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr("fill", lightMode ? "#f1f5f9" : "#0f172a")
      .attr("cursor", "default");

    const svgGroup = svg.append("g");

    // Crear enlaces - CORREGIDO: Usar enter/exit/update pattern
    const link = svgGroup.append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(filteredData.links, d => `${d.source}-${d.target}`)
      .join(
        enter => enter.append("line")
          .attr("class", "link-line")
          .attr("stroke", d => d.color || (lightMode ? "rgba(100, 116, 139, 0.6)" : "rgba(148, 163, 184, 0.6)"))
          .attr("stroke-width", 2)
          .attr("stroke-opacity", 0.8),
        update => update,
        exit => exit.remove()
      );

    // Crear nodos
    const node = svgGroup.append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(filteredData.nodes, d => d.id)
      .join(
        enter => enter.append("g")
          .attr("class", "node-group")
          .call(d3.drag()
            .on("start", dragstarted)
            .on("drag", dragged)
            .on("end", dragended)),
        update => update,
        exit => exit.remove()
      );

    // Círculo del nodo
    node.selectAll("circle").remove();
    const nodeCircles = node.append("circle")
      .attr("class", "node-circle")
      .attr("r", 30)
      .attr("fill", d => d.color)
      .attr("stroke", d => d.isSelected ? "#ffffff" : (lightMode ? "rgba(0, 0, 0, 0.2)" : "rgba(255, 255, 255, 0.3)"))
      .attr("stroke-width", d => d.isSelected ? 3 : 1)
      .attr("filter", d => d.isSelected ? 
        "drop-shadow(0 0 25px rgba(59, 130, 246, 0.8))" : 
        (lightMode ? "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2))" : "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3))"))
      .attr("cursor", "pointer")
      .on("click", (event, d) => {
        event.stopPropagation();
        handleNodeClick(d);
      })
      .on("mouseover", function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("r", 35)
          .attr("stroke-width", 2)
          .attr("stroke", lightMode ? "rgba(0, 0, 0, 0.4)" : "rgba(255, 255, 255, 0.5)");
        
        // Resaltar links conectados
        link.attr("stroke-opacity", l => {
          const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
          const targetId = typeof l.target === 'object' ? l.target.id : l.target;
         // Usar filteredData.nodes en lugar de filteredNodes
   // Verificar si ambos nodos están actualmente visibles
  const sourceVisible = filteredData.nodes.some(n => n.id === sourceId);
  const targetVisible = filteredData.nodes.some(n => n.id === targetId);

  // Sólo mostrar la línea si ambos nodos están visibles
  return sourceVisible && targetVisible ? 0.8 : 0;
        });
      })
      .on("mouseout", function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("r", 30)
          .attr("stroke-width", d.isSelected ? 3 : 1)
          .attr("stroke", d => d.isSelected ? "#ffffff" : (lightMode ? "rgba(0, 0, 0, 0.2)" : "rgba(255, 255, 255, 0.3)"));
        
        link.attr("stroke-opacity", 0.8);
      });

    // Texto dentro del nodo - NOMBRE COMPLETO
    node.selectAll("text").remove();
    node.append("text")
      .attr("class", "node-name")
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .attr("font-size", "9px")
      .attr("font-weight", "600")
      .attr("fill", lightMode ? "#000000" : "#ffffff")
      .attr("pointer-events", "none")
      .text(d => {
        const maxLength = 12;
        const name = d.name;
        if (name.length > maxLength) {
          return name.substring(0, maxLength-3) + "...";
        }
        return name;
      });

    // Crear simulación de fuerza - CORREGIDO
    const simulation = d3.forceSimulation(filteredData.nodes)
      .force("link", d3.forceLink(filteredData.links)
        .id(d => d.id)
        .distance(100)
        .strength(0.5))
      .force("charge", d3.forceManyBody()
        .strength(-80))
      .force("center", d3.forceCenter(width / 2, height / 2))
.force("x", d3.forceX(width / 2).strength(0.01))
.force("y", d3.forceY(height / 2).strength(0.01))
      .force("collide", d3.forceCollide().radius(35))
      .alphaTarget(0.3)
      .restart()
      .on("tick", ticked);

    simulationRef.current = simulation;

    function ticked() {
      // Actualizar posiciones de los links
      link
        .attr("x1", d => {
          const sourceNode = typeof d.source === 'object' ? d.source : filteredData.nodes.find(n => n.id === d.source);
          return sourceNode ? sourceNode.x : width / 2;
        })
        .attr("y1", d => {
          const sourceNode = typeof d.source === 'object' ? d.source : filteredData.nodes.find(n => n.id === d.source);
          return sourceNode ? sourceNode.y : height / 2;
        })
        .attr("x2", d => {
          const targetNode = typeof d.target === 'object' ? d.target : filteredData.nodes.find(n => n.id === d.target);
          return targetNode ? targetNode.x : width / 2;
        })
        .attr("y2", d => {
          const targetNode = typeof d.target === 'object' ? d.target : filteredData.nodes.find(n => n.id === d.target);
          return targetNode ? targetNode.y : height / 2;
        });

      // Actualizar posiciones de los nodos
      node.attr("transform", d => `translate(${d.x || width/2},${d.y || height/2})`);
    }

    function dragstarted(event) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    // Zoom
    const zoom = d3.zoom()
      .scaleExtent([0.3, 5])
      .on("zoom", (event) => {
        svgGroup.attr("transform", event.transform);
      });
    
    svg.call(zoom);

    // Inicializar posiciones si no las tienen
    filteredData.nodes.forEach((node, i) => {
      if (node.x == null || node.y == null) {
        node.x = width / 2 + (Math.random() - 0.5) * 100;
        node.y = height / 2 + (Math.random() - 0.5) * 100;
      }
    });

    // Forzar un tick inicial
    simulation.tick(50);

    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
    };
  }, [filteredData, lightMode, handleNodeClick]);

  const maxRangeValue = Math.ceil((new Date(fechaFinal) - new Date(fechaInicial)) / (1000 * 60 * 60 * 24));
  const currentRangeValue = Math.floor((new Date(fechaSeleccionada) - new Date(fechaInicial)) / (1000 * 60 * 60 * 24));

  const handleFechaChange = (event) => {
    const days = parseInt(event.target.value, 10);
    const newFecha = new Date(fechaInicial);
    newFecha.setDate(newFecha.getDate() + days);
    setFechaSeleccionada(newFecha.toISOString().split('T')[0]);
  };

  const handleFechaMinChange = (e) => {
    setFechaInicial(e.target.value);
    if (new Date(e.target.value) > new Date(fechaSeleccionada)) {
      setFechaSeleccionada(e.target.value);
    }
  };

  const handleFechaMaxChange = (e) => {
    setFechaFinal(e.target.value);
    if (new Date(e.target.value) < new Date(fechaSeleccionada)) {
      setFechaSeleccionada(e.target.value);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No disponible";
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? "No disponible" : date.toLocaleDateString('es-ES', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return "No disponible";
    }
  };

  const controlFieldLabels = {
    dni: "DNI",
    nombre: "Nombre",
    apellido: "Apellido",
    edad: "Edad",
    sexo: "Sexo",
    genero: "Género",
    conviviente: "Conviviente",
    temperatura: "Temperatura",
    peso: "Peso",
    talla: "Talla",
    imc: "IMC",
    periodicidad: "Periodicidad",
    tipo: "Tipo",
    descripcion: "Descripción",
    dosis: "Dosis",
    fecha: "Fecha",
    resultado: "Resultado",
    observacion: "Observación",
    observaciones: "Observaciones",
    intolerancias: "Intolerancias",
    alergias: "Alergias",
  };

  const humanizeControlLabel = useCallback((key) => {
    if (!key && key !== 0) return "Dato";

    const normalizedKey = String(key).trim();
    if (controlFieldLabels[normalizedKey]) {
      return controlFieldLabels[normalizedKey];
    }

    if (/^id[A-Z]/.test(normalizedKey) || /^Id[A-Z]/.test(normalizedKey)) {
      return normalizedKey.replace(/^id|^Id/, "").replace(/([A-Z])/g, " $1").trim();
    }

    return normalizedKey
      .replace(/[_-]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }, []);

  const parseControlJsonData = useCallback((jsonData) => {
    if (typeof jsonData === "string") {
      try {
        return JSON.parse(jsonData);
      } catch (error) {
        return {};
      }
    }

    return jsonData && typeof jsonData === "object" ? jsonData : {};
  }, []);

  const hasRenderableValue = useCallback((value) => {
    if (value === null || value === undefined || value === "" || value === false) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.some((item) => hasRenderableValue(item));
    }

    if (typeof value === "object") {
      return Object.values(value).some((item) => hasRenderableValue(item));
    }

    return true;
  }, []);

  const formatControlValue = useCallback((value) => {
    if (value === null || value === undefined || value === "") {
      return "Sin dato";
    }

    if (typeof value === "boolean") {
      return value ? "Sí" : "No";
    }

    if (Array.isArray(value)) {
      return value
        .filter((item) => hasRenderableValue(item))
        .map((item) => formatControlValue(item))
        .filter(Boolean)
        .join(", ");
    }

    if (typeof value === "object") {
      return Object.entries(value)
        .filter(([, nestedValue]) => hasRenderableValue(nestedValue))
        .map(([nestedKey, nestedValue]) => `${humanizeControlLabel(nestedKey)}: ${formatControlValue(nestedValue)}`)
        .join(" | ");
    }

    return String(value);
  }, [hasRenderableValue, humanizeControlLabel]);

  const buildLookupMaps = useCallback(async (idPatologia) => {
    const safeGet = async (request) => {
      try {
        return await request;
      } catch (error) {
        console.error("Error al cargar catálogo del seguimiento:", error);
        return { data: [] };
      }
    };

    const [sintomasResp, vacunasResp, medicacionesResp, estudiosResp] = await Promise.all([
      safeGet(api.get("sintomas-por-patologia")),
      safeGet(api.get("vacunas-por-patologia")),
      safeGet(api.get("medicaciones-por-patologia")),
      idPatologia ? safeGet(api.get(`estudios-patologia/${idPatologia}`)) : Promise.resolve({ data: [] }),
    ]);

    const sintomasMap = {};
    const vacunasMap = {};
    const medicacionesMap = {};
    const estudiosMap = {};

    (Array.isArray(sintomasResp.data) ? sintomasResp.data : []).forEach((item) => {
      const nombre = item.NombreSintoma || item.Nombre || item.descripcion;
      if (item.IdSintomaPorPatologia != null) sintomasMap[item.IdSintomaPorPatologia] = nombre || `Síntoma ${item.IdSintomaPorPatologia}`;
      if (item.IdSintoma != null) sintomasMap[item.IdSintoma] = nombre || `Síntoma ${item.IdSintoma}`;
    });

    (Array.isArray(vacunasResp.data) ? vacunasResp.data : []).forEach((item) => {
      const nombre = item.NombreVacuna || item.Nombre;
      if (item.IdVacunacionPorPatologia != null) vacunasMap[item.IdVacunacionPorPatologia] = nombre || `Vacuna ${item.IdVacunacionPorPatologia}`;
      if (item.IdVacunacion != null) vacunasMap[item.IdVacunacion] = nombre || `Vacuna ${item.IdVacunacion}`;
    });

    (Array.isArray(medicacionesResp.data) ? medicacionesResp.data : []).forEach((item) => {
      const nombre = item.NombreMedicacion || item.Nombre;
      if (item.IdMedicacionPorPatologia != null) medicacionesMap[item.IdMedicacionPorPatologia] = nombre || `Tratamiento ${item.IdMedicacionPorPatologia}`;
      if (item.IdMedicacion != null) medicacionesMap[item.IdMedicacion] = nombre || `Tratamiento ${item.IdMedicacion}`;
    });

    (Array.isArray(estudiosResp.data) ? estudiosResp.data : []).forEach((item) => {
      const nombre = item.NombreEstudio || item.Nombre;
      if (item.IdEstudio != null) estudiosMap[item.IdEstudio] = nombre || `Estudio ${item.IdEstudio}`;
    });

    return { sintomasMap, vacunasMap, medicacionesMap, estudiosMap };
  }, []);

  const buildNamedEntries = useCallback((data, lookupMap, fallbackPrefix) => {
    if (!data || typeof data !== "object") {
      return [];
    }

    return Object.entries(data)
      .filter(([, value]) => hasRenderableValue(value))
      .map(([key, value]) => ({
        label: lookupMap[key] || `${fallbackPrefix} ${key}`,
        value: typeof value === "boolean" ? "Registrado" : formatControlValue(value),
      }));
  }, [formatControlValue, hasRenderableValue]);

  const buildFriendlyControlData = useCallback(async (control) => {
    const jsonDatos = parseControlJsonData(control?.json_datos);
    const contacto = jsonDatos?.contacto && typeof jsonDatos.contacto === "object" ? jsonDatos.contacto : {};
    const idPatologia = contacto.idPatologia || contacto.IdPatologia || null;
    const lookupMaps = await buildLookupMaps(idPatologia);

    const contactoItems = [
      { label: "Nombre", value: contacto.nombre },
      { label: "DNI", value: contacto.dni },
      { label: "Edad", value: contacto.edad },
      { label: "Sexo", value: contacto.sexo },
      { label: "Género", value: contacto.genero },
      { label: "Conviviente", value: typeof contacto.conviviente === "boolean" ? (contacto.conviviente ? "Sí" : "No") : contacto.conviviente },
    ].filter((item) => hasRenderableValue(item.value));

    const antropometriaItems = [
      { label: "Peso", value: jsonDatos?.peso },
      { label: "Talla", value: jsonDatos?.talla },
      { label: "IMC", value: jsonDatos?.imc },
      { label: "Temperatura", value: contacto.temperatura || jsonDatos?.temperatura },
    ].filter((item) => hasRenderableValue(item.value));

    const sintomasItems = buildNamedEntries(jsonDatos?.sintomas, lookupMaps.sintomasMap, "Síntoma");
    const vacunasItems = buildNamedEntries(jsonDatos?.vacunas, lookupMaps.vacunasMap, "Vacuna");
    const tratamientoItems = buildNamedEntries(jsonDatos?.medicaciones, lookupMaps.medicacionesMap, "Tratamiento");
    const estudiosItems = buildNamedEntries(jsonDatos?.estudios, lookupMaps.estudiosMap, "Estudio");

    const adicionalesItems = [
      { label: "Intolerancias", value: jsonDatos?.intolerancias },
      { label: "Alergias", value: jsonDatos?.alergias },
    ].filter((item) => hasRenderableValue(item.value));

    return {
      contactoItems,
      antropometriaItems,
      sintomasItems,
      vacunasItems,
      tratamientoItems,
      estudiosItems,
      adicionalesItems,
    };
  }, [buildLookupMaps, buildNamedEntries, hasRenderableValue, parseControlJsonData]);

  const getTelefonosDesdeNodo = (node) => {
    const telefonosArray = node?.informacionCompleta?.Telefonos;
    if (Array.isArray(telefonosArray) && telefonosArray.length > 0) {
      return telefonosArray;
    }

    const telefonoRaw = node?.informacionCompleta?.Telefono;
    if (!telefonoRaw || typeof telefonoRaw !== "string") {
      return [];
    }

    return telefonoRaw
      .split(/[,;|/]+/)
      .map((telefono) => telefono.trim())
      .filter(Boolean);
  };

  const closeAllModals = () => {
    setModalIsOpen(false);
    setRelacionesModalOpen(false);
    setSeguimientoModalOpen(false);
    setSelectedNode(null);
    setUltimoControlData(null);
    setSeguimientoModalError("");
  };

  const handleOpenRelacionesModal = () => {
    setModalIsOpen(false);
    // Usar timeout para asegurar que el primer modal se cierra antes de abrir el segundo
    setTimeout(() => {
      setRelacionesModalOpen(true);
    }, 100);
  };

  const handleOpenSeguimiento = async () => {
    if (!selectedNode?.id) return;

    setSeguimientoLoading(true);
    setSeguimientoModalError("");
    setUltimoControlData(null);

    try {
      const idPersonaSeleccionada = selectedNode.id;

      let idSeguimiento = null;
      let seguimientoSeleccionado = null;

      try {
        const seguimientosResp = await api.get(`/seguimientos/persona/${idPersonaSeleccionada}`);
        const seguimientos = Array.isArray(seguimientosResp.data) ? seguimientosResp.data : [];

        if (seguimientos.length > 0) {
          const ordenados = [...seguimientos].sort((a, b) => {
            const fechaA = new Date(a.fechaInicio || a.createdAt || 0).getTime();
            const fechaB = new Date(b.fechaInicio || b.createdAt || 0).getTime();
            return fechaB - fechaA;
          });
          seguimientoSeleccionado = ordenados[0];
          idSeguimiento =
            seguimientoSeleccionado?.idSeguimiento ||
            seguimientoSeleccionado?.IdSeguimiento ||
            seguimientoSeleccionado?.id ||
            null;
        }
      } catch (seguimientosError) {
        console.error("Error al obtener seguimientos por persona:", seguimientosError);
      }

      if (!idSeguimiento) {
        try {
          const seguimientoCompletoResp = await api.get(`/seguimiento/persona/${idPersonaSeleccionada}`);
          const data = seguimientoCompletoResp?.data || {};
          idSeguimiento = data.idSeguimiento || data.IdSeguimiento || null;
        } catch (seguimientoCompletoError) {
          console.error("Error al obtener seguimiento completo:", seguimientoCompletoError);
        }
      }

      if (!idSeguimiento) {
        setSeguimientoModalError("La persona no tiene seguimiento registrado todavía.");
      } else {
        const controlesResp = await api.get(`/controles/${idSeguimiento}`);
        const controles = Array.isArray(controlesResp.data) ? controlesResp.data : [];

        if (controles.length === 0) {
          setUltimoControlData({
            idSeguimiento,
            seguimiento: seguimientoSeleccionado,
            ultimoControl: null,
          });
        } else {
          const ultimoControl = controles[0];
          const detalleControl = await buildFriendlyControlData(ultimoControl);
          setUltimoControlData({
            idSeguimiento,
            seguimiento: seguimientoSeleccionado,
            ultimoControl,
            detalleControl,
          });
        }
      }

      setModalIsOpen(false);
      setTimeout(() => {
        setSeguimientoModalOpen(true);
      }, 100);
    } catch (error) {
      console.error("Error al obtener último control del contacto:", error);
      setSeguimientoModalError("No se pudo cargar la información del seguimiento.");
      setModalIsOpen(false);
      setTimeout(() => {
        setSeguimientoModalOpen(true);
      }, 100);
    } finally {
      setSeguimientoLoading(false);
    }
  };

  const getModalStyles = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(6px)',
      zIndex: 1000
    },
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      maxWidth: '620px',
      width: '92%',
      maxHeight: '88vh',
      overflowY: 'auto',
      backgroundColor: '#ffffff',
      border: 'none',
      borderRadius: '20px',
      padding: '22px',
      boxShadow: '0 30px 80px rgba(0, 0, 0, 0.5)',
      fontFamily: "'Inter', sans-serif"
    }
  };

  const getSmallModalStyles = {
    ...getModalStyles,
    content: {
      ...getModalStyles.content,
      maxWidth: '520px'
    }
  };

  return (
    <ContainerGrafos className={lightMode ? "light-mode" : ""}>
      {/* Panel superior - Controles */}
      <Section
        className="graph-controls-section"
        compact 
        background="rgba(30, 41, 59, 0.95)"
        border 
        shadow 
        blur 
        zIndex={10}
      >
        <ControlsContainer>
          <ButtonBack onClick={handleClick}>
            ← Información de contactos
          </ButtonBack>
          
          <ControlGroup>
            <ControlLabel>Tipo de fecha</ControlLabel>
            <select
              value={tipoFecha}
              onChange={(event) => setTipoFecha(event.target.value)}
              style={{
                padding: '12px 16px',
                border: `2px solid ${lightMode ? '#cbd5e1' : '#334155'}`,
                borderRadius: '14px',
                background: lightMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 41, 59, 0.8)',
                color: lightMode ? '#1e293b' : 'white',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="FechaProbableExposicion">Probable Exposición</option>
              <option value="FechaInicioSintomas">Inicio Síntomas</option>
              <option value="FechaPrimeraConsulta">Primera Consulta</option>
            </select>
          </ControlGroup>

          <ControlGroup>
            <ControlLabel>Fecha Inicial</ControlLabel>
            <input 
              type="date" 
              value={fechaInicial} 
              onChange={handleFechaMinChange}
              style={{
                padding: '12px 16px',
                border: `2px solid ${lightMode ? '#cbd5e1' : '#334155'}`,
                borderRadius: '14px',
                background: lightMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 41, 59, 0.8)',
                color: lightMode ? '#1e293b' : 'white',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
          </ControlGroup>
          
          <ControlGroup>
            <ControlLabel>Fecha Final</ControlLabel>
            <input 
              type="date" 
              value={fechaFinal} 
              onChange={handleFechaMaxChange}
              style={{
                padding: '12px 16px',
                border: `2px solid ${lightMode ? '#cbd5e1' : '#334155'}`,
                borderRadius: '14px',
                background: lightMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 41, 59, 0.8)',
                color: lightMode ? '#1e293b' : 'white',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
          </ControlGroup>
          
          <ThemeToggleButton onClick={toggleTheme}>
            {lightMode ? '🌙 Modo Oscuro' : '☀️ Modo Claro'}
          </ThemeToggleButton>
        </ControlsContainer>
      </Section>

      {/* Área principal del gráfico */}
      <Section className="graph-main-section" style={{ flex: 1, position: 'relative', padding: '10px' }}>
        {loading && (
          <LoadingOverlay>
            <LoadingSpinner />
            <LoadingText>Cargando datos epidemiológicos...</LoadingText>
          </LoadingOverlay>
        )}
        
        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}

        {/* Leyenda de estados */}
        <LegendContainer>
          <LegendTitle>Estados Epidemiológicos</LegendTitle>
          <LegendItems>
            {getUniqueNodeColors(filteredData.nodes).map((item, index) => (
              <LegendItem key={index}>
                <LegendColor color={item.color} />
                <LegendText>{item.estado}</LegendText>
              </LegendItem>
            ))}
          </LegendItems>
        </LegendContainer>

        {/* Contenedor del gráfico */}
        <GraphContainer>
          <svg ref={svgRef}></svg>
        </GraphContainer>

        {/* Contador de nodos visibles */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          background: lightMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 41, 59, 0.9)',
          padding: '10px 20px',
          borderRadius: '12px',
          color: lightMode ? '#1e293b' : 'white',
          fontSize: '14px',
          border: `1px solid ${lightMode ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`
        }}>
          Nodos visibles: {visibleNodes.length} / {data.nodes.length} | 
          Conexiones: {filteredData.links.length}
        </div>
      </Section>

      {/* Modal para información de persona */}
      <Modal 
        isOpen={modalIsOpen} 
        onRequestClose={closeAllModals} 
        style={getModalStyles}
        ariaHideApp={false}
      >
        <ModalHeader>
          <ModalTitle>👤 Información Epidemiológica</ModalTitle>
          <ModalCloseButton onClick={closeAllModals}>×</ModalCloseButton>
        </ModalHeader>
        
        {selectedNode && selectedNode.informacionCompleta && (
          <div>
            <PersonaHeader>
              <PersonaAvatar color={selectedNode.color}>
                {selectedNode.name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </PersonaAvatar>
              <PersonaTitle>
                <h4>{selectedNode.name}</h4>
                <PersonaStatus color={selectedNode.color}>
                  {selectedNode.estado}
                </PersonaStatus>
              </PersonaTitle>
            </PersonaHeader>
            
            <InfoGrid>
              <InfoItem>
                <InfoLabel>📄 Documento</InfoLabel>
                <InfoValue>
                  {selectedNode.informacionCompleta.NroDocumento || "No disponible"}
                </InfoValue>
              </InfoItem>
              
              <InfoItem>
                <InfoLabel>🎂 Edad</InfoLabel>
                <InfoValue>
                  {selectedNode.informacionCompleta.Edad || "No disponible"}
                </InfoValue>
              </InfoItem>
              
              <InfoItem>
                <InfoLabel>⚧ Sexo</InfoLabel>
                <InfoValue>
                  {selectedNode.informacionCompleta.Sexo || "No disponible"}
                </InfoValue>
              </InfoItem>
              
              <InfoItem>
                <InfoLabel>📧 Email</InfoLabel>
                <InfoValue>
                  {selectedNode.informacionCompleta.Email || "No disponible"}
                </InfoValue>
              </InfoItem>

              <InfoItem fullWidth>
                <InfoLabel>🏠 Domicilio</InfoLabel>
                <InfoValue>
                  {selectedNode.informacionCompleta.Domicilio || "No disponible"}
                </InfoValue>
              </InfoItem>

              <InfoItem fullWidth>
                <InfoLabel>📞 Teléfono(s)</InfoLabel>
                <InfoValue>
                  {getTelefonosDesdeNodo(selectedNode).length > 0
                    ? getTelefonosDesdeNodo(selectedNode).join(" / ")
                    : "No disponible"}
                </InfoValue>
              </InfoItem>
              
              <InfoItem fullWidth>
                <InfoLabel>📅 Fecha de seguimiento (timeline)</InfoLabel>
                <InfoValue>
                  {formatDate(selectedNode.fechaCercana)}
                </InfoValue>
              </InfoItem>
            </InfoGrid>
            
            <div
              style={{
                marginTop: '30px',
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap'
              }}
            >
              <button
                onClick={handleOpenRelacionesModal}
                style={{
                  flex: '1 1 240px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '15px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                🔗 Ver Contactos Epidemiológicos
              </button>

              <button
                onClick={handleOpenSeguimiento}
                style={{
                  flex: '1 1 200px',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '15px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                📋 Ver Seguimiento
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal para relaciones epidemiológicas */}
      <Modal 
        isOpen={relacionesModalOpen} 
        onRequestClose={closeAllModals} 
        style={getSmallModalStyles}
        ariaHideApp={false}
      >
        <ModalHeader>
          <ModalTitle>🔗 Contactos Epidemiológicos</ModalTitle>
          <ModalCloseButton onClick={closeAllModals}>×</ModalCloseButton>
        </ModalHeader>
        
        <div style={{ padding: '20px 0' }}>
          {relacionesLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <InlineSpinner />
              <p style={{ color: '#64748b', margin: 0 }}>Cargando contactos...</p>
            </div>
          ) : relacionesData.length > 0 ? (
            <div>
              <div style={{ 
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', 
                color: 'white', 
                padding: '15px 20px', 
                borderRadius: '12px', 
                marginBottom: '25px', 
                textAlign: 'center' 
              }}>
                <p style={{ margin: 0, fontWeight: '600', fontSize: '16px' }}>
                  📊 Análisis Epidemiológico
                </p>
                <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                  {selectedNode?.name} ha tenido contacto con {relacionesData.length} persona(s)
                </p>
              </div>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
                gap: '15px', 
                maxHeight: '400px', 
                overflowY: 'auto', 
                padding: '10px' 
              }}>
                {relacionesData.map((relacion, index) => (
                  <div 
                    key={index}
                    style={{ 
                      background: 'white', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '16px', 
                      padding: '18px', 
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <div style={{ 
                        width: '50px', 
                        height: '50px', 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        color: 'white', 
                        fontWeight: '700', 
                        fontSize: '18px',
                        flexShrink: 0 
                      }}>
                        {relacion.personaRelacionada?.nombre?.[0] || '?'}{relacion.personaRelacionada?.apellido?.[0] || '?'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
                            {relacion.personaRelacionada?.nombre || 'Nombre'} {relacion.personaRelacionada?.apellido || 'Apellido'}
                          </span>
                        </div>
                        <span style={{ 
                          background: 'rgba(59, 130, 246, 0.1)', 
                          color: '#3b82f6', 
                          padding: '4px 10px', 
                          borderRadius: '20px', 
                          fontSize: '11px', 
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {relacion.tipoRelacion || 'Contacto'}
                        </span>
                      </div>
                    </div>
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#64748b', 
                      padding: '8px', 
                      background: '#f8fafc', 
                      borderRadius: '8px',
                      borderLeft: '3px solid #3b82f6'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>ID: {relacion.personaRelacionada?.IdPersona || 'N/A'}</span>
                        <span style={{ 
                          color: relacion.tipoRelacion === 'familiar' ? '#ef4444' : 
                                 relacion.tipoRelacion === 'colegas' ? '#f59e0b' : '#10b981',
                          fontWeight: '600'
                        }}>
                          {relacion.tipoRelacion === 'familiar' ? 'Alto Riesgo' : 
                           relacion.tipoRelacion === 'colegas' ? 'Medio Riesgo' : 'Bajo Riesgo'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                background: '#f8fafc', 
                borderRadius: '12px', 
                border: '1px solid #e2e8f0' 
              }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#475569', fontWeight: '600' }}>
                  📋 Resumen Epidemiológico:
                </p>
                <ul style={{ margin: '0', paddingLeft: '20px', color: '#64748b', fontSize: '13px' }}>
                  <li>Total de contactos: <strong>{relacionesData.length}</strong></li>
                  <li>Relaciones familiares: <strong>{relacionesData.filter(r => r.tipoRelacion === 'familiar').length}</strong></li>
                  <li>Contactos laborales: <strong>{relacionesData.filter(r => r.tipoRelacion === 'colegas').length}</strong></li>
                  <li>Otros contactos: <strong>{relacionesData.filter(r => !['familiar', 'colegas'].includes(r.tipoRelacion)).length}</strong></li>
                </ul>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px', opacity: 0.5 }}>🎯</div>
              <p style={{ fontSize: '16px', margin: 0, fontWeight: '500' }}>
                No se detectaron contactos epidemiológicos para esta persona.
              </p>
              <p style={{ fontSize: '14px', margin: '10px 0 0 0', color: '#94a3b8' }}>
                Esto podría indicar un caso primario o falta de datos de contacto.
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal para último control del seguimiento */}
      <Modal
        isOpen={seguimientoModalOpen}
        onRequestClose={closeAllModals}
        style={getSmallModalStyles}
        ariaHideApp={false}
      >
        <ModalHeader>
          <ModalTitle>📋 Último Control del Contacto</ModalTitle>
          <ModalCloseButton onClick={closeAllModals}>×</ModalCloseButton>
        </ModalHeader>

        <div style={{ padding: "16px 0" }}>
          {seguimientoLoading ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "30px" }}>
              <InlineSpinner />
              <p style={{ color: "#64748b", margin: 0 }}>Cargando último control...</p>
            </div>
          ) : seguimientoModalError ? (
            <div style={{ textAlign: "center", padding: "24px", color: "#64748b" }}>
              <p style={{ margin: 0, fontWeight: 600 }}>{seguimientoModalError}</p>
            </div>
          ) : ultimoControlData?.ultimoControl ? (
            <div style={{ display: "grid", gap: "12px" }}>
              <InfoItem>
                <InfoLabel>🔢 Número de control</InfoLabel>
                <InfoValue>{ultimoControlData.ultimoControl.numeroControl || "No disponible"}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>📅 Fecha de control</InfoLabel>
                <InfoValue>{formatDate(ultimoControlData.ultimoControl.fechaControl)}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>👤 Usuario</InfoLabel>
                <InfoValue>
                  {ultimoControlData.ultimoControl.usuario
                    ? `${ultimoControlData.ultimoControl.usuario.Nombre || ""} ${ultimoControlData.ultimoControl.usuario.Apellido || ""}`.trim() || ultimoControlData.ultimoControl.usuario.Username || "No disponible"
                    : "No disponible"}
                </InfoValue>
              </InfoItem>

              <InfoItem fullWidth>
                <InfoLabel>📝 Observaciones</InfoLabel>
                <InfoValue>{ultimoControlData.ultimoControl.observaciones || "Sin observaciones"}</InfoValue>
              </InfoItem>

              {ultimoControlData.detalleControl?.contactoItems?.length > 0 && (
                <ControlSectionCard>
                  <ControlSectionHeader>👤 Datos del contacto</ControlSectionHeader>
                  <ControlSectionGrid>
                    {ultimoControlData.detalleControl.contactoItems.map((item) => (
                      <ControlDetailCard key={`contacto-${item.label}`}>
                        <ControlDetailLabel>{item.label}</ControlDetailLabel>
                        <ControlDetailValue>{item.value}</ControlDetailValue>
                      </ControlDetailCard>
                    ))}
                  </ControlSectionGrid>
                </ControlSectionCard>
              )}

              {ultimoControlData.detalleControl?.antropometriaItems?.length > 0 && (
                <ControlSectionCard>
                  <ControlSectionHeader>📏 Datos del control</ControlSectionHeader>
                  <ControlSectionGrid>
                    {ultimoControlData.detalleControl.antropometriaItems.map((item) => (
                      <ControlDetailCard key={`antropometria-${item.label}`}>
                        <ControlDetailLabel>{item.label}</ControlDetailLabel>
                        <ControlDetailValue>{item.value}</ControlDetailValue>
                      </ControlDetailCard>
                    ))}
                  </ControlSectionGrid>
                </ControlSectionCard>
              )}

              {ultimoControlData.detalleControl?.sintomasItems?.length > 0 && (
                <ControlSectionCard>
                  <ControlSectionHeader>🤒 Síntomas</ControlSectionHeader>
                  <ControlSectionGrid>
                    {ultimoControlData.detalleControl.sintomasItems.map((item) => (
                      <ControlDetailCard key={`sintoma-${item.label}`}>
                        <ControlDetailLabel>{item.label}</ControlDetailLabel>
                        <ControlDetailValue>{item.value}</ControlDetailValue>
                      </ControlDetailCard>
                    ))}
                  </ControlSectionGrid>
                </ControlSectionCard>
              )}

              {ultimoControlData.detalleControl?.vacunasItems?.length > 0 && (
                <ControlSectionCard>
                  <ControlSectionHeader>💉 Vacunas</ControlSectionHeader>
                  <ControlSectionGrid>
                    {ultimoControlData.detalleControl.vacunasItems.map((item) => (
                      <ControlDetailCard key={`vacuna-${item.label}`}>
                        <ControlDetailLabel>{item.label}</ControlDetailLabel>
                        <ControlDetailValue>{item.value}</ControlDetailValue>
                      </ControlDetailCard>
                    ))}
                  </ControlSectionGrid>
                </ControlSectionCard>
              )}

              {ultimoControlData.detalleControl?.tratamientoItems?.length > 0 && (
                <ControlSectionCard>
                  <ControlSectionHeader>💊 Tratamiento</ControlSectionHeader>
                  <ControlSectionGrid>
                    {ultimoControlData.detalleControl.tratamientoItems.map((item) => (
                      <ControlDetailCard key={`tratamiento-${item.label}`}>
                        <ControlDetailLabel>{item.label}</ControlDetailLabel>
                        <ControlDetailValue>{item.value}</ControlDetailValue>
                      </ControlDetailCard>
                    ))}
                  </ControlSectionGrid>
                </ControlSectionCard>
              )}

              {ultimoControlData.detalleControl?.estudiosItems?.length > 0 && (
                <ControlSectionCard>
                  <ControlSectionHeader>🧪 Estudios</ControlSectionHeader>
                  <ControlSectionGrid>
                    {ultimoControlData.detalleControl.estudiosItems.map((item) => (
                      <ControlDetailCard key={`estudio-${item.label}`}>
                        <ControlDetailLabel>{item.label}</ControlDetailLabel>
                        <ControlDetailValue>{item.value}</ControlDetailValue>
                      </ControlDetailCard>
                    ))}
                  </ControlSectionGrid>
                </ControlSectionCard>
              )}

              {ultimoControlData.detalleControl?.adicionalesItems?.length > 0 && (
                <ControlSectionCard>
                  <ControlSectionHeader>🩺 Información adicional</ControlSectionHeader>
                  <ControlSectionGrid>
                    {ultimoControlData.detalleControl.adicionalesItems.map((item) => (
                      <ControlDetailCard key={`adicional-${item.label}`}>
                        <ControlDetailLabel>{item.label}</ControlDetailLabel>
                        <ControlDetailValue>{item.value}</ControlDetailValue>
                      </ControlDetailCard>
                    ))}
                  </ControlSectionGrid>
                </ControlSectionCard>
              )}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "24px", color: "#64748b" }}>
              <p style={{ margin: 0, fontWeight: 600 }}>No hay controles registrados para este contacto.</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Panel inferior - Control de tiempo */}
      <Section
        compact
        style={{
          width: 'calc(70% - 40px)',
          margin: '0 auto',
          padding: '4px 12px 6px 12px',
          position: 'absolute',
          bottom: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          height: 'auto',
          minHeight: '6px',
          maxHeight: '13px',
          backdropFilter: 'blur(8px)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          opacity: 0.95,
          transition: 'opacity 0.3s ease',
          ':hover': {
            opacity: 1,
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <TimelineControl style={{ 
          padding: '6px 10px',
          background: 'transparent' 
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '6px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PlayButton
                onClick={handlePlayPause}
                playing={isPlaying}
                disabled={currentRangeValue >= maxRangeValue}
                style={{ 
                  width: '30px',
                  height: '30px',
                  fontSize: '13px',
                  boxShadow: '0 3px 8px rgba(59, 130, 246, 0.2)'
                }}
              >
                {isPlaying ? '⏸' : '▶'}
              </PlayButton>
              
              <SpeedControl style={{ 
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <SpeedButton 
                  className={playSpeed === 1 ? 'active' : ''} 
                  onClick={() => handleSpeedChange(1)}
                  style={{ 
                    padding: '3px 8px',
                    fontSize: '10px',
                    borderRadius: '12px',
                    minWidth: '32px'
                  }}
                >
                  1x
                </SpeedButton>
                <SpeedButton 
                  className={playSpeed === 2 ? 'active' : ''} 
                  onClick={() => handleSpeedChange(2)}
                  style={{ 
                    padding: '3px 8px',
                    fontSize: '10px',
                    borderRadius: '12px',
                    minWidth: '32px'
                  }}
                >
                  2x
                </SpeedButton>
                <SpeedButton 
                  className={playSpeed === 5 ? 'active' : ''} 
                  onClick={() => handleSpeedChange(5)}
                  style={{ 
                    padding: '3px 8px',
                    fontSize: '10px',
                    borderRadius: '12px',
                    minWidth: '32px'
                  }}
                >
                  5x
                </SpeedButton>
              </SpeedControl>
            </div>
            
            <div style={{ 
              fontSize: '12px',
              color: '#94a3b8',
              fontWeight: '500',
              padding: '4px 10px',
              background: 'rgba(59, 130, 246, 0.1)',
              borderRadius: '14px',
              border: '1px solid rgba(59, 130, 246, 0.2)'
            }}>
              {fechaSeleccionada}
            </div>
          </div>
          
          <TimelineSlider
            min="0"
            max={maxRangeValue}
            value={currentRangeValue}
            onChange={handleFechaChange}
            style={{ 
              margin: '6px 0 4px 0',
              height: '4px',
              background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.3) 0%, rgba(59, 130, 246, 0.6) 100%)'
            }}
          />
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            fontSize: '10px',
            color: '#94a3b8',
            marginTop: '4px',
            fontWeight: '500'
          }}>
            <span style={{ opacity: 0.7 }}>{fechaInicial}</span>
            <span style={{ 
              color: '#3b82f6',
              fontWeight: '600',
              fontSize: '10px'
            }}>
              Día {currentRangeValue + 1} de {maxRangeValue + 1}
            </span>
            <span style={{ opacity: 0.7 }}>{fechaFinal}</span>
          </div>
        </TimelineControl>
      </Section>
    </ContainerGrafos>
  );
};

export default Grafos;
